import {
  type Appointment,
  type Task,
  deleteSchedule,
  deleteTask,
} from "@repo/api";
import { t } from "@repo/translation";
import { useToast } from "@repo/ui";
import { getErrorMessage } from "@repo/utils";
import { type ReactNode, createContext, useContext, useState } from "react";

import { type CalenderAppointmentsSearchFilters } from "./calendarFilter/appointmentFilterReducer";
import { type CoWorkerCalendarItem } from "./types";
import { type UpdateTaskData, updateTask } from "./updateTask";

interface Props {
  children: ReactNode;
  items: (Appointment | Task)[];
  appointmentFilters: CalenderAppointmentsSearchFilters;
  highlightedItemId?: string;
}

interface ICalendarContext {
  copyTask: (task: CoWorkerCalendarItem) => void;
  deleteTask: (taskId: number) => Promise<void>;
  deleteSchedule: (scheduleId: string) => Promise<void>;
  taskCopy?: CoWorkerCalendarItem;
  saveTask: (taskData: UpdateTaskData) => Promise<any>;
  isItemSelected: (id: string) => boolean;
  appointmentFilters: CalenderAppointmentsSearchFilters;
}

const CalendarContext = createContext<ICalendarContext>({
  copyTask: () => undefined,
  taskCopy: undefined,
  saveTask: () => Promise.resolve(true),
  deleteTask: () => Promise.resolve(),
  deleteSchedule: () => Promise.resolve(),
  isItemSelected: () => false,
  appointmentFilters: {} as CalenderAppointmentsSearchFilters,
});

export const CalendarProvider = ({
  children,
  items,
  appointmentFilters,
  highlightedItemId,
}: Props) => {
  const { updateToast } = useToast();
  const [taskCopy, setTaskCopy] = useState<CoWorkerCalendarItem | undefined>(
    undefined
  );

  const copyTask = (originalTask: CoWorkerCalendarItem) => {
    setTaskCopy(originalTask);
  };

  const saveTask = (taskData: UpdateTaskData) =>
    updateTask(
      items.find((el) => String(el.id) === taskData.id),
      taskData,
      updateToast
    );

  const onDeleteTask = (taskId: number): Promise<void> =>
    deleteTask(taskId)
      .then(() => {
        updateToast({
          id: taskId,
          title: t("prompt.task_deleted"),
        });
      })
      .catch((err) => {
        updateToast({
          id: taskId,
          title: t("prompt.task_delete_failed"),
          message: getErrorMessage(err),
          type: "error",
        });
      });

  const onDeleteSchedule = (scheduleId: string): Promise<void> => {
    return deleteSchedule(scheduleId)
      .then(() => {
        updateToast({
          id: scheduleId,
          title: t("prompt.schedule_deleted"),
        });
      })
      .catch((err) => {
        updateToast({
          id: scheduleId,
          title: t("prompt.schedule_delete_failed"),
          message: err.message,
          type: "error",
        });
      });
  };

  const isItemSelected = (id: string) =>
    taskCopy?.id === id || highlightedItemId === id;

  return (
    <CalendarContext.Provider
      value={{
        copyTask,
        saveTask,
        deleteTask: onDeleteTask,
        deleteSchedule: onDeleteSchedule,
        taskCopy,
        isItemSelected,
        appointmentFilters,
      }}
    >
      {children}
    </CalendarContext.Provider>
  );
};

export const useCalendarContext = () => useContext(CalendarContext);
